import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import PlansManager from "../components/PlansManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCreditCard,
  faChartLine,
  faKey,
  faEnvelope,
  faCamera,
  faCheck,
  faBusinessTime,
  faCoins,
  faIdBadge,
} from "@fortawesome/free-solid-svg-icons";

// UI Components
import { SkeletonVariants } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { CinematicHero, GlassSection, R5Tabs, R5FormPair, R5Field, R5Grid2, R5AvatarCard } from "../components/ui";
import MemberWallet from "../components/MemberWallet";
import TokenHistory from "../components/TokenHistory";
import TimezonePreference from "../components/TimezonePreference";
import { BusinessListingsApi } from "../services/FirebaseApiService";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../FirebaseConfig";

import "../styles/AccountSettings.css";

// Sign-in email + password are owned by Wix (the auth provider), so they're
// managed on the Wix account page, not here. Set this to your Wix members/
// account URL to make the "Change Email"/"Change Password" buttons deep-link
// there; if unset, they explain where to go instead.
const WIX_ACCOUNT_URL = process.env.REACT_APP_WIX_ACCOUNT_URL || "";

// Empty profile shape (real values load from users/{uid} + the auth record).
const EMPTY_PROFILE = {
  nickname: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  profileImage: "",
  location: "",
  bio: "",
  preferences: {
    emailNotifications: true,
    smsNotifications: false,
    newsletter: true,
  },
};

function AccountSettings() {
  const { firebaseUser } = useAuth();
  const plans = usePlans();
  const { memberOrders = [], loading: plansLoading = false, isBusinessOwner, PLAN_IDS } = plans || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [businessListings, setBusinessListings] = useState([]);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");

  const hasBusiness = useMemo(() => {
    if (typeof isBusinessOwner === "function" && isBusinessOwner()) return true;
    const businessPlan = PLAN_IDS?.BUSINESS_OWNER || "Ship Captain (Business Owner)";
    return memberOrders.some((order) => (
      order?.status === "ACTIVE" &&
      (order?.planName === businessPlan || order?.planId === businessPlan)
    ));
  }, [isBusinessOwner, memberOrders, PLAN_IDS]);

  const businessSummary = useMemo(() => {
    if (!businessListings || businessListings.length === 0) {
      return {
        totalListings: 0,
        activeListings: 0,
        pendingListings: 0,
        promotedListings: 0,
        totalViews: 0,
        totalReviews: 0,
        averageRating: 0,
        setupCompletion: 0,
        primaryListing: null,
      };
    }

    const totalListings = businessListings.length;
    const activeListings = businessListings.filter((l) => l.status === "active").length;
    const pendingListings = businessListings.filter((l) => l.status === "pending").length;
    const promotedListings = businessListings.filter((l) => (
      l?.promotion?.isActive && l?.promotion?.expiresAt && new Date(l.promotion.expiresAt) > new Date()
    )).length;

    const totalViews = businessListings.reduce((sum, listing) => sum + Number(listing?.viewCount || 0), 0);
    const totalReviews = businessListings.reduce((sum, listing) => sum + Number(listing?.ratingCount || 0), 0);
    const weightedRatings = businessListings.reduce((sum, listing) => {
      const count = Number(listing?.ratingCount || 0);
      const rating = Number(listing?.averageRating || 0);
      return sum + (rating * count);
    }, 0);
    const averageRating = totalReviews > 0 ? (weightedRatings / totalReviews) : 0;

    const completedProfiles = businessListings.filter((listing) => (
      Boolean(listing?.name) &&
      Boolean(listing?.category) &&
      Boolean(listing?.description) &&
      Array.isArray(listing?.images) && listing.images.length > 0
    )).length;
    const setupCompletion = Math.round((completedProfiles / totalListings) * 100);

    const primaryListing = businessListings.find((l) => l.status === "active") || businessListings[0];

    return {
      totalListings,
      activeListings,
      pendingListings,
      promotedListings,
      totalViews,
      totalReviews,
      averageRating,
      setupCompletion,
      primaryListing,
    };
  }, [businessListings]);

  // Set the active section based on the current path
  useEffect(() => {
    const path = location.pathname.split("/").pop();

    switch (path) {
      case "profile":
        setActiveSection("profile");
        break;
      case "subscriptions":
        setActiveSection("subscriptions");
        break;
      case "business":
        setActiveSection("business");
        break;
      case "analytics":
        setActiveSection("analytics");
        break;
      case "tokens":
        setActiveSection("tokens");
        break;
      case "wallet":
        setActiveSection("wallet");
        break;
      default:
        setActiveSection("profile");
    }
  }, [location]);

  // Load the real signed-in user's profile from users/{uid} + the auth record.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!firebaseUser?.uid) {
        if (!cancelled) { setProfile(EMPTY_PROFILE); setLoading(false); }
        return;
      }
      try {
        const snap = await getDoc(doc(firestore, "users", firebaseUser.uid));
        const d = snap.exists() ? snap.data() : {};
        if (cancelled) return;
        setProfile({
          nickname: d.displayName || d.nickname || firebaseUser.displayName || "",
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          // Email is sourced from the sign-in provider (read-only here).
          email: d.email || firebaseUser.email || "",
          phone: d.phoneNumber || d.phone || "",
          profileImage: d.photoURL || firebaseUser.photoURL || "",
          location: d.location || "",
          bio: d.bio || "",
          preferences: {
            emailNotifications: d.preferences?.emailNotifications ?? true,
            smsNotifications: d.preferences?.smsNotifications ?? false,
            newsletter: d.preferences?.newsletter ?? true,
          },
        });
      } catch (err) {
        console.error("Failed to load profile:", err);
        if (!cancelled) setProfile(EMPTY_PROFILE);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Re-load only when the signed-in user changes; other firebaseUser fields
    // are read as one-time fallbacks and shouldn't retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser?.uid]);

  // Persist the editable, app-owned profile fields to users/{uid}. Email is
  // deliberately NOT written here — it's owned by the sign-in provider and
  // needs a verified change flow (see the Change Email note).
  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setPref = (k, v) =>
    setProfile((p) => ({ ...p, preferences: { ...p.preferences, [k]: v } }));

  // Sign-in email + password live with Wix — send the user there to change
  // them (verified change + account recovery are handled by Wix), rather than
  // faking an in-app flow that would revert at next login.
  const manageSignIn = (what) => {
    if (WIX_ACCOUNT_URL) {
      window.open(WIX_ACCOUNT_URL, "_blank", "noopener,noreferrer");
      return;
    }
    window.alert(
      `Your sign-in ${what} is managed through your account login (Wix), where the ` +
        `change is verified and can be recovered if you lose access. Once your ` +
        `account-management link is configured, this button will take you straight there.`
    );
  };

  const handleSaveProfile = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!firebaseUser?.uid || saving || !profile) return;
    setSaving(true);
    setSaveMsg("");
    try {
      await setDoc(
        doc(firestore, "users", firebaseUser.uid),
        {
          displayName: profile.nickname || "",
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          phoneNumber: profile.phone || "",
          location: profile.location || "",
          bio: profile.bio || "",
          preferences: profile.preferences,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveMsg("Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadBusinessListings = async () => {
      if (!hasBusiness || !firebaseUser?.uid) {
        setBusinessListings([]);
        return;
      }

      setBusinessLoading(true);
      try {
        const listings = await BusinessListingsApi.getByOwner(firebaseUser.uid);
        setBusinessListings(Array.isArray(listings) ? listings : []);
      } catch (error) {
        console.error("Failed to load business listings for account:", error);
        setBusinessListings([]);
      } finally {
        setBusinessLoading(false);
      }
    };

    loadBusinessListings();
  }, [firebaseUser?.uid, hasBusiness]);

  // Navigate to a section
  const navigateToSection = (section) => {
    navigate(`/account/${section}`);
  };

  const navigateToBusinessDashboard = (tab = "overview") => {
    navigate(`/business-dashboard?tab=${tab}`);
  };

  const navigateToCreateListing = () => {
    navigate("/edit-business-listing/new");
  };

  const navigateToEditPrimaryListing = () => {
    if (!businessSummary.primaryListing?.id) return;
    navigate(`/edit-business-listing/${businessSummary.primaryListing.id}`);
  };

  // Render profile form
  const renderProfileForm = () => {
    if (!profile) return null;

    const initials = (profile.nickname || profile.firstName || "?")
      .trim()
      .charAt(0)
      .toUpperCase();

    return (
      <form className="profile-form account-form" onSubmit={handleSaveProfile}>
        {/* R5-M1 two-column layout: Edit Profile (main) + Avatar/Quick Stats (aside). */}
        <R5FormPair
          main={
            <>
              <h3>Edit Profile</h3>
              <R5Grid2>
                <R5Field label="Nickname">
                  <input type="text" value={profile.nickname} onChange={(e) => setField("nickname", e.target.value)} />
                </R5Field>
                <R5Field label="Email">
                  <input type="email" value={profile.email} disabled readOnly />
                </R5Field>
                <R5Field label="First Name">
                  <input type="text" value={profile.firstName} onChange={(e) => setField("firstName", e.target.value)} />
                </R5Field>
                <R5Field label="Last Name">
                  <input type="text" value={profile.lastName} onChange={(e) => setField("lastName", e.target.value)} />
                </R5Field>
                <R5Field label="Phone">
                  <input type="tel" value={profile.phone} onChange={(e) => setField("phone", e.target.value)} />
                </R5Field>
                <R5Field label="Location">
                  <input type="text" value={profile.location} onChange={(e) => setField("location", e.target.value)} />
                </R5Field>
              </R5Grid2>
              <R5Field label="Bio">
                <textarea rows="3" value={profile.bio} onChange={(e) => setField("bio", e.target.value)}></textarea>
              </R5Field>
              <button
                type="button"
                className="change-email-btn"
                style={{ marginBottom: 8 }}
                onClick={() => manageSignIn("email address")}
                title="Your sign-in email is managed by your account login (Wix)"
              >
                <FontAwesomeIcon icon={faEnvelope} /> Change Email
              </button>
            </>
          }
          aside={
            <>
              <R5AvatarCard
                initials={initials}
                name={profile.nickname}
                role={profile.location}
                image={
                  profile.profileImage ? (
                    <img
                      src={profile.profileImage}
                      alt={profile.nickname}
                      style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                    />
                  ) : null
                }
              />
              <h3>Quick Stats</h3>
              <R5Field label="Email">
                <input value={profile.email || ""} disabled readOnly />
              </R5Field>
              <R5Field label="Phone">
                <input value={profile.phone || ""} disabled readOnly />
              </R5Field>
              <button type="button" className="change-photo-btn r5-pack-cta" style={{ width: "100%" }}>
                <FontAwesomeIcon icon={faCamera} /> Change Photo
              </button>
            </>
          }
        />

        {/* Timezone — persisted to users/{uid}.timezone; powers tz-aware promo cart. */}
        <TimezonePreference />

        {/* Notification preferences — prod-only, no M1 equivalent. Kept as-is. */}
        <div className="form-section" style={{ marginTop: 22 }}>
          <h3>Notification Preferences</h3>

          <div className="preference-item">
            <div className="preference-label">Email Notifications</div>
            <label className="switch">
              <input
                type="checkbox"
                checked={profile.preferences.emailNotifications}
                onChange={(e) => setPref("emailNotifications", e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-label">SMS Notifications</div>
            <label className="switch">
              <input
                type="checkbox"
                checked={profile.preferences.smsNotifications}
                onChange={(e) => setPref("smsNotifications", e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-label">Weekly Newsletter</div>
            <label className="switch">
              <input
                type="checkbox"
                checked={profile.preferences.newsletter}
                onChange={(e) => setPref("newsletter", e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        {/* Security — prod-only, no M1 equivalent. Kept as-is. */}
        <div className="form-section">
          <h3>Security</h3>
          <button
            type="button"
            className="change-password-btn"
            onClick={() => manageSignIn("password")}
            title="Your password is managed by your account login (Wix)"
          >
            <FontAwesomeIcon icon={faKey} /> Change Password
          </button>
        </div>

        <div className="form-buttons" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button type="submit" className="save-profile-btn r5-pack-cta r5-pack-cta--bright" disabled={saving}>
            <FontAwesomeIcon icon={faCheck} /> {saving ? "Saving…" : "Save Changes"}
          </button>
          {saveMsg && (
            <span
              role="status"
              style={{ fontSize: 14, color: saveMsg === "Saved" ? "var(--status-success, #4a7c46)" : "var(--brand-oxblood, #b91c1c)" }}
            >
              {saveMsg}
            </span>
          )}
        </div>
      </form>
    );
  };

  // Render business profile form
  const renderBusinessForm = () => {
    if (plansLoading) {
      return <SkeletonVariants.Card />;
    }

    if (!hasBusiness) {
      return (
        <EmptyState
          icon={faBusinessTime}
          title="Business Profile"
          description="Ye need a Ship Captain (Business Owner) subscription to manage a business profile and listings."
          actionText="Upgrade to Business"
          onAction={() => navigateToSection("subscriptions")}
        />
      );
    }

    if (businessLoading) {
      return <SkeletonVariants.Card />;
    }

    if (businessSummary.totalListings === 0) {
      return (
        <div className="business-command-center">
          <div className="business-command-header">
            <h2>Business Profile Command Center</h2>
            <span className="business-subscription-pill">Business Subscription Active</span>
          </div>
          <p className="business-command-subtitle">
            Your subscription is active. Next step: create your first listing so travelers can discover and redeem with your business.
          </p>
          <div className="business-command-actions">
            <button className="save-business-btn" type="button" onClick={navigateToCreateListing}>
              <FontAwesomeIcon icon={faBusinessTime} /> Create First Listing
            </button>
            <button className="change-password-btn" type="button" onClick={() => navigateToBusinessDashboard("overview")}>
              <FontAwesomeIcon icon={faChartLine} /> Open Business Dashboard
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="business-command-center">
        <div className="business-command-header">
          <h2>Business Profile Command Center</h2>
          <span className="business-subscription-pill">Business Subscription Active</span>
        </div>

        <div className="business-command-metrics">
          <div className="analytics-card">
            <div className="card-title">Listings</div>
            <div className="card-value">{businessSummary.totalListings}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Active Listings</div>
            <div className="card-value">{businessSummary.activeListings}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Setup Completion</div>
            <div className="card-value">{businessSummary.setupCompletion}%</div>
          </div>
          <div className="analytics-card highlight">
            <div className="card-title">Total Views</div>
            <div className="card-value">{businessSummary.totalViews}</div>
          </div>
        </div>

        <div className="business-command-next">
          <h3>Recommended Next Steps</h3>
          <ul>
            <li>Keep listing details fresh to improve traveler trust and conversions.</li>
            <li>Configure redeemable items so your Doubloon offers are actionable.</li>
            <li>Use promotions to increase listing visibility during peak travel windows.</li>
          </ul>
        </div>

        <div className="business-command-actions">
          <button className="save-business-btn" type="button" onClick={() => navigateToBusinessDashboard("overview")}>
            <FontAwesomeIcon icon={faChartLine} /> Open Business Dashboard
          </button>
          <button className="change-password-btn" type="button" onClick={() => navigateToBusinessDashboard("redeemables")}>
            <FontAwesomeIcon icon={faCoins} /> Manage Redeemables
          </button>
          <button className="change-password-btn" type="button" onClick={() => navigateToBusinessDashboard("staff")}>
            <FontAwesomeIcon icon={faIdBadge} /> Manage Staff
          </button>
          <button className="change-password-btn" type="button" onClick={navigateToCreateListing}>
            <FontAwesomeIcon icon={faBusinessTime} /> Add New Listing
          </button>
          <button
            className="change-password-btn"
            type="button"
            onClick={navigateToEditPrimaryListing}
            disabled={!businessSummary.primaryListing?.id}
          >
            <FontAwesomeIcon icon={faCamera} /> Manage Primary Listing
          </button>
        </div>
      </div>
    );
  };

  // Render analytics
  const renderAnalytics = () => {
    if (plansLoading) {
      return <SkeletonVariants.Card />;
    }

    if (!hasBusiness) {
      return (
        <div className="business-upgrade-needed">
          <h2>Business Analytics</h2>
          <p>You need a business subscription to access analytics.</p>
          <button className="upgrade-btn" type="button" onClick={() => navigateToSection("subscriptions")}>Upgrade to Business</button>
        </div>
      );
    }

    if (businessLoading) {
      return <SkeletonVariants.Card />;
    }

    const engagementEvents = businessSummary.totalReviews + businessSummary.totalViews;
    const actionReadiness = businessSummary.promotedListings > 0
      ? "Promotion active"
      : "No active promotion";

    return (
      <div className="analytics-dashboard">
        <h2>Business Analytics Snapshot</h2>
        <p className="business-command-subtitle">
          This view is your quick pulse check. Use Business Dashboard for deep analytics, promotion management, and listing operations.
        </p>

        <div className="analytics-cards">
          <div className="analytics-card">
            <div className="card-title">Listings</div>
            <div className="card-value">{businessSummary.totalListings}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Active Listings</div>
            <div className="card-value">{businessSummary.activeListings}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Listing Views</div>
            <div className="card-value">{businessSummary.totalViews}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Avg Rating</div>
            <div className="card-value">{businessSummary.averageRating.toFixed(1)}</div>
          </div>
          <div className="analytics-card">
            <div className="card-title">Engagement Signal</div>
            <div className="card-value">{engagementEvents}</div>
          </div>
          <div className="analytics-card highlight">
            <div className="card-title">Promotion Status</div>
            <div className="card-value">{actionReadiness}</div>
          </div>
        </div>

        <div className="chart-container">
          <h3>Next Best Actions</h3>
          <ul className="business-action-list">
            {businessSummary.promotedListings === 0 ? <li>No promotions are active. Launch one from Business Dashboard to increase visibility.</li> : null}
            {businessSummary.setupCompletion < 100 ? <li>Some listings are missing category, description, or images. Complete profiles to improve conversion.</li> : null}
            {businessSummary.pendingListings > 0 ? <li>You have pending listings. Review listing status and publish blockers.</li> : null}
            {businessSummary.totalListings > 0 && businessSummary.totalViews < 100 ? <li>Views are modest. Refresh media and run a promotion campaign.</li> : null}
          </ul>
          <div className="business-command-actions">
            <button type="button" className="save-business-btn" onClick={() => navigateToBusinessDashboard("overview")}>
              <FontAwesomeIcon icon={faChartLine} /> Open Business Dashboard
            </button>
            <button type="button" className="change-password-btn" onClick={() => navigateToBusinessDashboard("promotion")}>Launch Promotion</button>
            <button type="button" className="change-password-btn" onClick={() => navigateToBusinessDashboard("redemption-analytics")}>View Redemption Analytics</button>
            <button type="button" className="change-password-btn" onClick={() => navigateToSection("business")}>Business Profile Setup</button>
          </div>
        </div>
      </div>
    );
  };

  const renderTokenHistory = () => (
    <div className="account-token-history-panel">
      <div className="account-token-cta">
        <div>
          <h3>Need more Doubloons for your next redemption?</h3>
          <p>
            Top up now to stay ready for participating offers and faster in-venue checkout.
          </p>
        </div>
        <div className="account-token-cta__actions">
          <button
            type="button"
            className="r5-pack-cta r5-pack-cta--bright"
            onClick={() => navigate('/tokens?src=account-history&intent=topup')}
          >
            Top up Doubloons
          </button>
          <button
            type="button"
            className="r5-pack-cta"
            onClick={() => navigate('/account/wallet')}
          >
            Open wallet pass
          </button>
        </div>
      </div>
      <TokenHistory userId={firebaseUser?.uid} />
    </div>
  );

  // Main render
  const accountTabs = [
    { key: "profile", label: "Profile", icon: <FontAwesomeIcon icon={faUser} /> },
    { key: "wallet", label: "Wallet & Pass", icon: <FontAwesomeIcon icon={faIdBadge} /> },
    { key: "subscriptions", label: "Subscriptions", icon: <FontAwesomeIcon icon={faCreditCard} /> },
    { key: "business", label: "Business Profile", icon: <FontAwesomeIcon icon={faBusinessTime} /> },
    { key: "analytics", label: "Analytics", icon: <FontAwesomeIcon icon={faChartLine} /> },
    { key: "tokens", label: "Doubloon History", icon: <FontAwesomeIcon icon={faCoins} /> },
  ];

  const heroSubtitle = profile && profile.nickname
    ? `Signed in as ${profile.nickname}`
    : "Manage your profile, subscriptions, and rewards";

  return (
    <>
      <CinematicHero
        eyebrow="· Account ·"
        title="Captain's Quarters"
        dropCap="C"
        lede={heroSubtitle}
      />
      <R5Tabs
        tabs={accountTabs}
        activeKey={activeSection}
        onChange={navigateToSection}
      />
      <GlassSection className="account-settings-page">
      <div className="account-content">
          {loading || plansLoading ? (
            <div className="loading-skeleton">
              <SkeletonVariants.Profile />
              <SkeletonVariants.Card />
              <SkeletonVariants.Card />
            </div>
          ) : (
            <Routes>
              <Route path="/" element={renderProfileForm()} />
              <Route path="/profile" element={renderProfileForm()} />
              <Route path="/wallet" element={<MemberWallet />} />
              <Route path="/subscriptions" element={<PlansManager />} />
              <Route path="/business" element={renderBusinessForm()} />
              <Route path="/analytics" element={renderAnalytics()} />
              <Route path="/tokens" element={renderTokenHistory()} />
            </Routes>
          )}
        </div>
      </GlassSection>
    </>
  );
}

export default AccountSettings;
